/**
 * Guards routes that require a signed-in user (opt in per route with
 * `canActivate: [AuthGuard]` in app.module.ts) - the counterpart of LoginGuard,
 * which guards the login route itself. Without it, such a page (e.g.
 * /settings/usersunassigned) could be opened by URL while signed out.
 *
 * Access is decided by the server-confirmed session (AuthService.ensureAuthenticated
 * -> /api/Account/session/status), never by localStorage alone, since that is only
 * a display cache. The server is asked once per page load, not on every navigation;
 * after that, AuthService keeps the status in sync (logout, 401, other tabs).
 */
import { Injectable, Injector } from '@angular/core';
import { CanActivate, CanActivateChild, Router, UrlTree } from '@angular/router';
import { AuthService } from './auth.service';
import { AppEnvService } from './services/app-env.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {
  // AuthService is resolved through the Injector after appEnv.load(), not
  // constructor-injected - same bootstrap-ordering reason as LoginGuard.
  constructor(private router: Router, private appEnv: AppEnvService, private injector: Injector) {}

  async canActivate(): Promise<boolean | UrlTree> {
    await this.appEnv.load();
    const authService = this.injector.get(AuthService);
    const isAuthenticated = await authService.ensureAuthenticated();
    return isAuthenticated ? true : this.router.parseUrl('/');
  }

  canActivateChild(): Promise<boolean | UrlTree> {
    return this.canActivate();
  }
}
